import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useScroll, useSpring, motion, useReducedMotion } from 'framer-motion';
import Seo from '@/components/Seo';
import Intro from '@/components/Intro';
import Header from '@/components/Header';
import ImmersiveJourney from '@/components/ImmersiveJourney';

gsap.registerPlugin(ScrollTrigger);
import Marquee from '@/components/Marquee';
import ReadyNow from '@/components/ReadyNow';
import Explore from '@/components/Explore';
import Homes from '@/components/Homes';
import Plans from '@/components/Plans';
import Connectivity from '@/components/Connectivity';
import Specs from '@/components/Specs';
import Enquiry from '@/components/Enquiry';
import Footer from '@/components/Footer';
import BottomNav from '@/components/BottomNav';
import Admin from '@/components/Admin';
import { CONTACT, applyContentOverrides } from '@/content';
import '@/App.css';

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const smooth = useSpring(scrollYProgress, { stiffness: 90, damping: 24 });
  const [dash, setDash] = useState(126);
  useEffect(() => smooth.on('change', (v) => setDash(126 - 126 * v)), [smooth]);
  return (
    <div className="pointer-events-none fixed right-4 top-1/2 z-40 hidden -translate-y-1/2 lg:block" aria-hidden="true">
      <svg width="46" height="46" viewBox="0 0 46 46" className="-rotate-90">
        <circle cx="23" cy="23" r="20" fill="none" stroke="#E4E6EB" strokeWidth="2.5" />
        <circle cx="23" cy="23" r="20" fill="none" stroke="#C92F4B" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="126" strokeDashoffset={dash} />
      </svg>
    </div>
  );
}

function Microsite() {
  const [introDone, setIntroDone] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryPreset, setEnquiryPreset] = useState(null);
  // published CMS overrides are merged into content.js before any section renders
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    const draft = new URLSearchParams(window.location.search).get('preview') === 'draft';
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content${draft ? '?draft=1' : ''}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.content) applyContentOverrides(d.content); })
      .catch(() => {})
      .finally(() => setContentReady(true));
  }, []);

  const openEnquiry = (preset) => {
    setEnquiryPreset(preset || null);
    setEnquiryOpen(true);
  };

  if (!contentReady) return <div className="min-h-screen bg-[#FAF9F6]" />;

  return (
    <div className="bg-[#FAF9F6] pb-[calc(64px+env(safe-area-inset-bottom))] md:pb-0">
      <Seo
        title="Codename Metro One Navade — Life in a Metro One | Ready 1 & 2 BHK Homes at Aarohi Complex"
        siteName="Metro One"
        description="Life in a Metro One — ready-to-move, OC-received 1 & 2 BHK ventilated homes at Aarohi Complex, Navade, Navi Mumbai. Customized semi-furnished and fully furnished options. Ready today, connected to tomorrow. MahaRERA: P52000015331. Call +91 74002 45025."
        image="/assets/hero-elevation.webp"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ApartmentComplex',
          name: 'Aarohi Complex (Codename Metro One — Navade)',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Plot No. 156, Phase-II, Navade',
            addressLocality: 'Navi Mumbai',
            addressRegion: 'Maharashtra',
            postalCode: '410208',
            addressCountry: 'IN',
          },
          telephone: CONTACT.phone,
        }}
      />

      {!introDone && <Intro onDone={() => setIntroDone(true)} />}

      <Header onEnquire={() => openEnquiry()} />
      <ScrollProgress />

      <main>
        <ImmersiveJourney onEnquire={openEnquiry} />
        <Marquee />
        <ReadyNow onEnquire={() => openEnquiry()} />
        <Explore />
        <Homes />
        <Plans onEnquire={openEnquiry} />
        <Connectivity />
        <Specs />
      </main>

      <Footer onEnquire={openEnquiry} />
      <BottomNav onEnquire={() => openEnquiry()} />

      <Enquiry open={enquiryOpen} preset={enquiryPreset} onClose={() => setEnquiryOpen(false)} />
    </div>
  );
}

export default function App() {
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
    window.__lenis = lenis; // test automation hook (e2e scroll positioning)
    lenis.on('scroll', ScrollTrigger.update);
    // idle-aware rAF: the loop sleeps after 1.4s without scroll input — zero idle CPU/battery cost
    let running = false;
    let idleTimer = null;
    const tick = (t) => lenis.raf(t * 1000);
    const sleep = () => {
      running = false;
      gsap.ticker.remove(tick);
    };
    const wake = () => {
      if (!running) {
        running = true;
        gsap.ticker.add(tick);
      }
      clearTimeout(idleTimer);
      idleTimer = setTimeout(sleep, 1400);
    };
    ['wheel', 'touchstart', 'touchmove', 'keydown'].forEach((ev) => window.addEventListener(ev, wake, { passive: true }));
    wake();
    gsap.ticker.lagSmoothing(0);
    const onAnchor = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const el = document.querySelector(a.getAttribute('href'));
      if (el) {
        e.preventDefault();
        wake();
        lenis.scrollTo(el, { offset: -64 });
      }
    };
    document.addEventListener('click', onAnchor);
    return () => {
      clearTimeout(idleTimer);
      gsap.ticker.remove(tick);
      ['wheel', 'touchstart', 'touchmove', 'keydown'].forEach((ev) => window.removeEventListener(ev, wake));
      lenis.destroy();
      document.removeEventListener('click', onAnchor);
    };
  }, [reduced]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Microsite />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}
